export function dealToFlatMap(
  deal: any,
  payrollDetails?: {
    departments: any[];
    totals: any;
    flat?: Record<string, any>;
  },
): Record<string, unknown> {
  const kpis = deal?.kpis ?? {};
  const fnb = kpis.fnb ?? {};
  const capture = fnb.capture ?? {};
  const check = fnb.check ?? {};
  const guestCheck = check.guest ?? {};
  const externalCheck = check.external ?? {};
  const externalPerDay = fnb.external?.perDay ?? {};
  const otherRev = kpis.otherRev ?? {};
  const wellness = otherRev.wellness ?? {};
  const other = otherRev.other ?? {};

  const opex = kpis.opex ?? {};
  const payroll = kpis.payroll ?? {};
  const roomRev = kpis.roomRevenue ?? {};
  const adr: number[] = roomRev.adr ?? [];
  const occ: number[] = roomRev.occ ?? [];
  const budget = kpis.investmentBudget ?? {};
  const ramp = kpis.rampSettings ?? {};

  const revenueRamp: number[] = ramp.revenueRamp ?? [];
  const costRamp: number[] = ramp.costRamp ?? [];

  const financing = kpis.financing ?? {};
  const exit = kpis.exit ?? {};
  const amenities = kpis.amenities ?? {};

  let roomTypes: any[] = [];
  const roomMixData = kpis.roomMix ?? [];

  if (Array.isArray(roomMixData)) {
    roomTypes = roomMixData;
  } else if (
    roomMixData &&
    typeof roomMixData === "object" &&
    Array.isArray(roomMixData.types)
  ) {
    roomTypes = roomMixData.types;
  }

  const maxRooms = 5;
  const normalizedRoomTypes = roomTypes.slice(0, maxRooms).map((rt) => ({
    ...rt,
    count: Number(rt?.count ?? rt?.rooms ?? rt?.numberOfRooms ?? 0),
    name: rt?.name ?? rt?.type ?? "",
  }));

  // Total rooms: try roomMix sum first, fall back to explicit fields
  const totalRoomsFromMix = normalizedRoomTypes.reduce(
    (sum, rt) => sum + rt.count,
    0,
  );
  const totalRooms =
    totalRoomsFromMix > 0
      ? totalRoomsFromMix
      : Number(
          kpis.rooms?.total ??
            kpis.roomsTotal ??
            kpis.totalRooms ??
            kpis.numberOfRooms ??
            0,
        );

  const flat: Record<string, unknown> = {
    // ========== PROPERTY DETAILS ==========
    name: deal.name ?? "",
    location: kpis.location ?? "",
    "kpis.fullAddress": kpis.address ?? kpis.address ?? "",
    "kpis.propertyType": kpis.propertyType ?? "",
    currency: deal.currency ?? "USD",

    "kpis.gfaSqm": kpis.gfaSqm ?? 0,
    "kpis.stars": kpis.stars ?? kpis.starRating ?? 0,

    // D14-D18 — room type counts
    "kpis.roomMix.0.count": normalizedRoomTypes[0]?.count ?? 0,
    "kpis.roomMix.1.count": normalizedRoomTypes[1]?.count ?? 0,
    "kpis.roomMix.2.count": normalizedRoomTypes[2]?.count ?? 0,
    "kpis.roomMix.3.count": normalizedRoomTypes[3]?.count ?? 0,
    "kpis.roomMix.4.count": normalizedRoomTypes[4]?.count ?? 0,

    // D19 — Total Rooms = SUM(D14:D18)
    // "kpis.rooms.total": totalRooms,

    // Facilities
    "kpis.property.facilities.0": (deal.kpis?.facilities ?? []).includes(
      "restaurant",
    )
      ? "yes"
      : "no",
    "kpis.property.facilities.1": (deal.kpis?.facilities ?? []).includes("bar")
      ? "yes"
      : "no",
    "kpis.property.facilities.2": (deal.kpis?.facilities ?? []).includes(
      "roomservice",
    )
      ? "yes"
      : "no",
    "kpis.property.facilities.3": (deal.kpis?.facilities ?? []).includes("spa")
      ? "yes"
      : "no",
    "kpis.property.facilities.4": (deal.kpis?.facilities ?? []).includes(
      "parking",
    )
      ? "yes"
      : "no",
    "kpis.property.facilities.5": (deal.kpis?.facilities ?? []).includes(
      "meetings",
    )
      ? "yes"
      : "no",
    "kpis.property.facilities.6": (deal.kpis?.facilities ?? []).includes("pool")
      ? "yes"
      : "no",
    "kpis.property.facilities.7": (deal.kpis?.facilities ?? []).includes("gym")
      ? "yes"
      : "no",

    // ========== INVESTMENT BUDGET ==========
    "kpis.investmentBudget.netPurchasePrice": budget.netPurchasePrice ?? 0,
    "kpis.investmentBudget.reTransferTax": budget.reTransferTax ?? 0,
    "kpis.investmentBudget.dealCosts": budget.dealCosts ?? 0,
    "kpis.investmentBudget.siteAcquisition": budget.siteAcquisition ?? 0,
    "kpis.investmentBudget.constructionCosts": budget.constructionCosts ?? 0,
    "kpis.investmentBudget.ffeOse": budget.ffeOse ?? 0,
    "kpis.investmentBudget.professionalFees": budget.professionalFees ?? 0,
    "kpis.investmentBudget.planningCharges": budget.planningCharges ?? 0,
    "kpis.investmentBudget.developmentFee": budget.developmentFee ?? 0,
    "kpis.investmentBudget.insuranceAdmin": budget.insuranceAdmin ?? 0,
    "kpis.investmentBudget.otherDev": budget.otherDev ?? 0,
    "kpis.investmentBudget.preOpening": budget.preOpening ?? 0,
    "kpis.investmentBudget.contingencyAmount": budget.contingencyAmount ?? 0,
    "kpis.investmentBudget.grandTotal": budget.grandTotal ?? 0,

    // ========== ROOM REVENUE ==========
    "kpis.roomRev.adr.0": adr[0] ?? 0,
    "kpis.roomRev.adr.1": adr[1] ?? 0,
    "kpis.roomRev.adr.2": adr[2] ?? 0,
    "kpis.roomRev.adr.3": adr[3] ?? 0,
    "kpis.roomRev.adr.4": adr[4] ?? 0,
    "kpis.roomRev.adr.5": adr[5] ?? 0,
    "kpis.roomRev.adr.6": adr[6] ?? 0,
    "kpis.roomRev.adr.7": adr[7] ?? 0,
    "kpis.roomRev.adr.8": adr[8] ?? 0,
    "kpis.roomRev.adr.9": adr[9] ?? 0,
    "kpis.roomRev.adr.10": adr[10] ?? 0,
    "kpis.roomRev.adr.11": adr[11] ?? 0,

    "kpis.roomRev.occ.0": occ[0] ?? 0,
    "kpis.roomRev.occ.1": occ[1] ?? 0,
    "kpis.roomRev.occ.2": occ[2] ?? 0,
    "kpis.roomRev.occ.3": occ[3] ?? 0,
    "kpis.roomRev.occ.4": occ[4] ?? 0,
    "kpis.roomRev.occ.5": occ[5] ?? 0,
    "kpis.roomRev.occ.6": occ[6] ?? 0,
    "kpis.roomRev.occ.7": occ[7] ?? 0,
    "kpis.roomRev.occ.8": occ[8] ?? 0,
    "kpis.roomRev.occ.9": occ[9] ?? 0,
    "kpis.roomRev.occ.10": occ[10] ?? 0,
    "kpis.roomRev.occ.11": occ[11] ?? 0,

    // ========== F&B REVENUE ==========
    "kpis.fnb.avgGuests": fnb.avgGuests ?? 2.2,

    "kpis.fnb.capture.breakfast": capture.breakfast ?? 0,
    "kpis.fnb.check.guest.breakfast": guestCheck.breakfast ?? 0,
    "kpis.fnb.external.perDay.breakfast": externalPerDay.breakfast ?? 0,
    "kpis.fnb.check.external.breakfast": externalCheck.breakfast ?? 0,

    "kpis.fnb.capture.lunch": capture.lunch ?? 0,
    "kpis.fnb.check.guest.lunch": guestCheck.lunch ?? 0,
    "kpis.fnb.external.perDay.lunch": externalPerDay.lunch ?? 0,
    "kpis.fnb.check.external.lunch": externalCheck.lunch ?? 0,

    "kpis.fnb.capture.dinner": capture.dinner ?? 0,
    "kpis.fnb.check.guest.dinner": guestCheck.dinner ?? 0,
    "kpis.fnb.external.perDay.dinner": externalPerDay.dinner ?? 0,
    "kpis.fnb.check.external.dinner": externalCheck.dinner ?? 0,

    "kpis.fnb.capture.bar": capture.bar ?? 0,
    "kpis.fnb.check.guest.bar": guestCheck.bar ?? 0,
    "kpis.fnb.external.perDay.bar": externalPerDay.bar ?? 0,
    "kpis.fnb.check.external.bar": externalCheck.bar ?? 0,

    // ========== OTHER REVENUE ==========
    "kpis.otherRev.wellness.treatmentsPerDay": Math.floor(
      Number(wellness.treatmentsPerDay ?? 0),
    ),
    "kpis.otherRev.wellness.avgPrice": wellness.avgPrice ?? 0,

    "kpis.otherRev.other.value":
      other.mode === "pctRooms" ? (other.value ?? 0) : 0, // → D12 (% of Rooms)
    "kpis.otherRev.other.monthlyFixed":
      other.mode === "fixed" ? (other.value ?? 0) : 0,

    // ========== PAYROLL ==========
    "kpis.payroll.totalFTEs": payroll.totalFTEs ?? 0,
    "kpis.payroll.totalAnnual": payroll.totalAnnual ?? 0,
    "kpis.payroll.socialChargesPercent": payroll.socialChargesPercent ?? 0,

    // ========== OPERATING COSTS ==========
    "kpis.opex.roomsCommission": opex.roomsCommission ?? 0.15,
    "kpis.opex.guestSuppliesCleaning": opex.guestSuppliesCleaning ?? 8,
    "kpis.opex.cogs": opex.cogs ?? 0.3,
    "kpis.opex.meetingsEvents": opex.meetingsEvents ?? 0.05,
    "kpis.opex.wellnessOther": opex.wellnessOther ?? 1000,
    "kpis.opex.otherDirect": opex.otherDirect ?? 2000,

    "kpis.opex.adminGeneralOther": opex.adminGeneralOther ?? 0.04,
    "kpis.opex.techSubscriptions": opex.techSubscriptions ?? 2000,
    "kpis.opex.salesMarketingOther": opex.salesMarketingOther ?? 0.03,
    "kpis.opex.maintenanceOther": opex.maintenanceOther ?? 0.02,
    "kpis.opex.utilities": opex.utilities ?? 0.03,

    "kpis.opex.mgmtFee": opex.mgmtFee ?? 0.03,
    "kpis.opex.propertyTaxes": opex.propertyTaxes ?? 0.01,
    "kpis.opex.insurance": opex.insurance ?? 0.005,
    "kpis.opex.rent": opex.rent ?? 5000,

    // ========== RAMP & MACRO ==========
    "kpis.rampSettings.revenueRamp.0": revenueRamp[0] ?? 0,
    "kpis.rampSettings.revenueRamp.1": revenueRamp[1] ?? 0,
    "kpis.rampSettings.revenueRamp.2": revenueRamp[2] ?? 0,
    "kpis.rampSettings.revenueRamp.3": revenueRamp[3] ?? 0,

    "kpis.rampSettings.costRamp.0": costRamp[0] ?? 0,
    "kpis.rampSettings.costRamp.1": costRamp[1] ?? 0,
    "kpis.rampSettings.costRamp.2": costRamp[2] ?? 0,
    "kpis.rampSettings.costRamp.3": costRamp[3] ?? 0,

    "kpis.rampSettings.toplineGrowthPct": ramp.toplineGrowthPct ?? 0,
    "kpis.rampSettings.inflationPct": ramp.inflationPct ?? 0,
    "kpis.rampSettings.depreciationPctOfCapex":
      ramp.depreciationPctOfCapex ?? 0,

    // ========== FINANCING ==========
    "kpis.financing.ltcPct": financing.ltcPct ?? 0.8,
    "kpis.financing.interestRatePct":
      financing.interestRatePct ?? financing.interestRate ?? 0.05,
    "kpis.financing.amortYears": financing.amortYears ?? 25,
    "kpis.financing.taxRateOnEBT": financing.taxRateOnEBT ?? 0.25,

    // ========== EXIT STRATEGY ==========
    "kpis.exit.holdPeriodYears":
      exit.sale?.exitYear ?? exit.holdPeriodYears ?? 7,
    "kpis.exit.exitCapRate": exit.sale?.exitCapRate ?? exit.exitCapRate ?? 5,
    "kpis.exit.sellingCostsPct":
      exit.sale?.sellingCostsPct ?? exit.sellingCostsPct ?? 2,
  };

  // ========== ДОБАВЛЯЕМ PAYROLL ДЕТАЛИ ==========
  if (payrollDetails?.flat) {
    Object.entries(payrollDetails.flat).forEach(([key, value]) => {
      flat[key] = value;
    });
  }

  return flat;
}
